import type { DialogProps as AriaDialogProps } from 'react-aria-components';
import { Dialog as AriaDialog } from 'react-aria-components';

import { cx } from '../../../utils/cx';
import { CloseButton } from '../../base/buttons/close-button';

/**
 * ModalContainer - Dialog wrapper with rounded corners, shadow, and background
 * Combines React Aria Dialog (for accessibility) with styled container
 * Optionally includes a close button when onClose prop is provided
 *
 * @example
 * ```tsx
 * <ModalContainer onClose={() => setOpen(false)}>
 *   <ModalHeader>...</ModalHeader>
 *   <ModalContent>...</ModalContent>
 *   <ModalFooter>...</ModalFooter>
 * </ModalContainer>
 * ```
 */
export const ModalContainer = ({
  children,
  className,
  onClose,
  ...props
}: AriaDialogProps & { onClose?: () => void }) => (
  <AriaDialog
    {...props}
    className={cx(
      'relative w-full overflow-hidden rounded-xl bg-primary shadow-xl outline-hidden',
      className,
    )}
  >
    {(renderProps) => (
      <>
        {onClose && (
          <CloseButton
            onClick={onClose}
            size="lg"
            className="absolute top-4 right-4 z-10"
            label="Close modal"
          />
        )}
        {typeof children === 'function' ? children(renderProps) : children}
      </>
    )}
  </AriaDialog>
);
